import { useCallback, useEffect, useRef, useState } from "react";
import { scopedIdbGet as idbGet, scopedIdbSet as idbSet } from "@/lib/client-scoped-idb";
import type { StressZoneId } from "./stress-test-content";

/**
 * `useCardPlacement` — manages the `cardId → zoneId | null` map for
 * the WS4 stress-test activity, hydrating from / persisting to
 * `idb-keyval` keyed by `(workshopId, "stress-test")`.
 *
 * `null` means the card sits in the unplaced deck. A missing key
 * means the same — consumers should treat `undefined` and `null`
 * interchangeably when reading.
 *
 * Persistence is best-effort: on hydrate / write failures we keep
 * the in-memory state and continue. The hook intentionally avoids
 * `console.log` per the repo conventions.
 *
 * Vanilla source: `_legacy/xaes-nav-wd.js` lines 672-743 — the
 * vanilla implementation kept placement state in the DOM only, so
 * the IDB persistence layer is a Phase 2 addition (the workshop
 * dashboard needs to remember placements across navigations + page
 * reloads).
 */

export type PlacementMap = Record<string, StressZoneId | null>;

export type UseCardPlacementResult = {
  placements: PlacementMap;
  /** Move `cardId` to `zone`, or pass `null` to return it to the deck. */
  place: (cardId: string, zone: StressZoneId | null) => void;
  /** Clear every placement — equivalent to vanilla's "RESET BOARD". */
  reset: () => void;
  /**
   * Wholesale replace placements with a snapshot's persisted map.
   * Used by the snapshot restore flow — see
   * `src/lib/use-snapshot.ts`.
   */
  replaceAll: (next: PlacementMap) => void;
  /** True until the first IDB read resolves. */
  hydrated: boolean;
};

function storageKey(workshopId: string): string {
  return `${workshopId}:stress-test`;
}

export function useCardPlacement(workshopId: string): UseCardPlacementResult {
  const [placements, setPlacements] = useState<PlacementMap>({});
  const [hydrated, setHydrated] = useState(false);
  // Track the latest hydration request — if the workshopId changes
  // mid-flight, we ignore any stale resolved values.
  const hydrationToken = useRef(0);

  useEffect(() => {
    const token = ++hydrationToken.current;
    setHydrated(false);
    let cancelled = false;

    idbGet<PlacementMap>(storageKey(workshopId))
      .then((stored) => {
        if (cancelled || hydrationToken.current !== token) return;
        setPlacements(stored && typeof stored === "object" ? stored : {});
        setHydrated(true);
      })
      .catch(() => {
        if (cancelled || hydrationToken.current !== token) return;
        // Read failed (e.g. IDB unavailable) — fall back to an empty
        // map but still mark as hydrated so the UI is interactive.
        setPlacements({});
        setHydrated(true);
      });

    return () => {
      cancelled = true;
    };
  }, [workshopId]);

  // Persist after every mutation (only once hydration has resolved so
  // we never overwrite stored state with the empty initial value).
  useEffect(() => {
    if (!hydrated) return;
    idbSet(storageKey(workshopId), placements).catch(() => {
      // Best-effort persistence — silently ignore write failures.
    });
  }, [hydrated, placements, workshopId]);

  const place = useCallback(
    (cardId: string, zone: StressZoneId | null): void => {
      setPlacements((prev) => {
        const current = prev[cardId];
        // Treat undefined and null as equivalent (both = unplaced).
        if (current === zone || (current == null && zone == null)) {
          return prev;
        }
        return { ...prev, [cardId]: zone };
      });
    },
    [],
  );

  const reset = useCallback((): void => {
    setPlacements({});
  }, []);

  const replaceAll = useCallback((next: PlacementMap): void => {
    // Defensive copy + filter — restore-time payloads come from
    // user-controlled snapshot JSON. Anything that doesn't look like
    // a StressZoneId is coerced to `null` so a malformed snapshot
    // can't poison the live placement map.
    const safe: PlacementMap = {};
    for (const [cardId, zone] of Object.entries(next ?? {})) {
      if (typeof cardId !== "string") continue;
      if (
        zone === "resist" ||
        zone === "concern" ||
        zone === "open" ||
        zone === "embrace"
      ) {
        safe[cardId] = zone;
      } else {
        safe[cardId] = null;
      }
    }
    setPlacements(safe);
  }, []);

  return { placements, place, reset, replaceAll, hydrated };
}
